
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Investor, Contact } from "@/types/investor";
import { generateEmailContent } from "@/utils/aiUtils";
import { getAPIKey } from "@/utils/ai/apiKeyUtils";
import { CustomEmail } from "@/components/emails/ContactSelector";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/integrations/supabase/client";
import { useGoogleAuth } from "@/contexts/GoogleAuthContext";

export const useEmailGenerator = (
  investor: Investor,
  contacts: Contact[],
  businessInfo: string,
  isLoading: boolean,
  setIsLoading: (loading: boolean) => void
) => {
  const { supabaseSession } = useGoogleAuth();
  const [generatedEmail, setGeneratedEmail] = useState("");
  const [selectedContact, setSelectedContact] = useState<string>("");
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [selectedContactData, setSelectedContactData] = useState<Contact | null>(null);
  const [customEmails, setCustomEmails] = useState<CustomEmail[]>([]);
  const [isCustomContact, setIsCustomContact] = useState(false);

  // Load custom emails from Supabase
  useEffect(() => {
    const loadCustomEmails = async () => {
      if (supabaseSession?.user?.id) {
        try {
          // Type assert to ensure TypeScript knows the structure we expect
          type CustomEmailRow = {
            id: string;
            user_id: string;
            name: string;
            email: string;
          };
          
          const { data, error } = await supabase
            .from('custom_emails')
            .select('id, name, email, user_id')
            .eq('user_id', supabaseSession.user.id);
            
          if (error) {
            console.error("Error loading custom emails:", error);
            setCustomEmails([]);
            return;
          }
          
          if (data && data.length > 0) {
            // Type assertion to help TypeScript understand the structure
            const customEmailsData = (data as CustomEmailRow[]).map(item => ({
              id: item.id,
              name: item.name,
              email: item.email
            }));
            setCustomEmails(customEmailsData);
          } else {
            setCustomEmails([]);
          }
        } catch (error) {
          console.error("Error loading custom emails:", error);
          setCustomEmails([]);
        }
      } else {
        setCustomEmails([]);
      }
    };
    
    loadCustomEmails();
  }, [supabaseSession]);

  // Update filtered contacts when investor or contacts change
  useEffect(() => {
    console.log("EmailGenerator - Investor firm:", investor.firm);
    console.log("EmailGenerator - All contacts:", contacts);
    
    // Filter contacts that match the investor's firm - case insensitive matching
    const matchingContacts = contacts.filter(contact => {
      // Handle potential undefined values
      const contactFirm = contact.firmName?.toLowerCase() || '';
      const investorFirm = investor.firm?.toLowerCase() || '';
      
      return contactFirm === investorFirm || 
        (investor.matchedContactIds && investor.matchedContactIds.includes(contact.id));
    });
    
    console.log('EmailGenerator - Filtered contacts:', matchingContacts);
    
    setFilteredContacts(matchingContacts);
    
    // Auto-select the first contact if none is selected
    if (matchingContacts.length > 0 && !selectedContact) {
      setSelectedContact(matchingContacts[0].id);
      setSelectedContactData(matchingContacts[0]);
      setIsCustomContact(false);
    }
  }, [investor, contacts, selectedContact]);

  // Update selected contact data when selectedContact changes
  useEffect(() => {
    if (selectedContact) {
      // Check if it's a custom email (custom emails have "custom-" prefix)
      if (selectedContact.startsWith("custom-")) {
        const customEmail = customEmails.find(c => c.id === selectedContact);
        if (customEmail) {
          setIsCustomContact(true);
          setSelectedContactData({
            id: customEmail.id,
            name: customEmail.name,
            email: customEmail.email,
            firmName: investor.firm || "",
            role: "Custom Contact"
          } as Contact);
        }
      } else {
        setIsCustomContact(false);
        const contact = filteredContacts.find(c => c.id === selectedContact);
        if (contact) {
          setSelectedContactData(contact);
        }
      }
    }
  }, [selectedContact, filteredContacts, customEmails, investor.firm]);

  const handleGenerateEmail = async () => {
    // Check if we have OpenAI API key stored
    const openAiKey = getAPIKey("openai");
    
    if (!openAiKey) {
      toast.error("Please configure your OpenAI API key in the API Connections section");
      return;
    }

    if (!selectedContactData) {
      toast.error("Please select a contact or add a custom email");
      return;
    }

    try {
      setIsLoading(true);
      const emailContent = await generateEmailContent(
        investor,
        selectedContactData,
        businessInfo,
        openAiKey
      );
      setGeneratedEmail(emailContent);
    } catch (error) {
      console.error("Error generating email:", error);
      toast.error("Failed to generate email. Please check your API key in the API Connections section.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactChange = (contactId: string) => {
    setSelectedContact(contactId);
  };

  const addCustomEmail = async (name: string, email: string) => {
    if (!supabaseSession?.user?.id) {
      toast.error("You must be logged in to add custom contacts");
      return null;
    }
    
    const customEmailId = `custom-${uuidv4()}`;
    
    const newCustomEmail: CustomEmail = {
      id: customEmailId,
      name,
      email
    };

    try {
      // Save to Supabase
      // TypeScript will raise an error about type compatibility, but we know
      // this is correctly structured for our 'custom_emails' table
      const { error } = await supabase
        .from('custom_emails')
        .insert([{
          id: customEmailId,
          user_id: supabaseSession.user.id,
          name,
          email
        }] as any); // Using type assertion to avoid TypeScript errors
        
      if (error) {
        console.error("Error saving custom email:", error);
        toast.error("Failed to save custom contact");
        return null;
      }
    
      // Update local state
      const updatedCustomEmails = [...customEmails, newCustomEmail];
      setCustomEmails(updatedCustomEmails);
      
      // Select the newly added custom email
      setSelectedContact(newCustomEmail.id);
      setIsCustomContact(true);
      
      return newCustomEmail;
    } catch (error) {
      console.error("Error adding custom email:", error);
      toast.error("Failed to add custom contact");
      return null;
    }
  };

  return {
    generatedEmail,
    setGeneratedEmail,
    selectedContact,
    filteredContacts,
    selectedContactData,
    customEmails,
    isCustomContact,
    handleGenerateEmail,
    handleContactChange,
    addCustomEmail,
    setSelectedContactData,
    setIsCustomContact
  };
};
